import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { FullPageFormLayout, Heading } from '@percent/lemonade'
import { WizardHeader } from '@percent/workplace-giving/common/components/WizardHeader/WizardHeader'
import {
  getAccountFromAuthState,
  getCountryCodeFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { useAuth, useLogger, useMutation } from '@percent/workplace-giving/common/hooks'
import {
  ORGANISATION_WEBSITE_MAX_LENGTH,
  useSharedValidationRules
} from '@percent/workplace-giving/common/hooks/useSharedValidationRules/useSharedValidationRules'
import { RequestNonprofitWizardForm } from './RequestNonprofitWizardForm'
import classes from './RequestNonprofitWizard.module.scss'
import { createNonprofitRequest } from '@percent/workplace-giving/api/nonprofit/createNonprofitRequest'
import { okResponse } from '@percent/workplace-giving/api/goodstack'
import { RequestNonprofitModal } from './RequestNonprofitModal/RequestNonprofitModal'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { volunteeringAllowedOrgCountriesAlpha3 } from '@percent/workplace-giving/common/data/sanctionedCountries'
import { getRequestNonprofitPrefill } from './requestNonprofitLink'

export function RequestNonprofitWizard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { state } = useAuth()
  const defaultCountry = getCountryCodeFromAuthState(state)!
  const prefill = getRequestNonprofitPrefill(searchParams)
  // A country this form doesn't offer (or none, e.g. "Global" on the match request form) falls back to the user's own.
  const initialCountry = volunteeringAllowedOrgCountriesAlpha3.includes(prefill.countryCode)
    ? prefill.countryCode
    : defaultCountry
  const { validateString, validateUrl, validateEmail } = useSharedValidationRules()
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false)
  const [openCancellationModal, setOpenCancellationModal] = useState<boolean>(false)
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false)
  const { track } = useAnalytics()
  const account = getAccountFromAuthState(state)

  const { mutateAsync } = useMutation({
    mutationFn: createNonprofitRequest,
    onSuccess: res => {
      if (okResponse(res)) {
        setOpenSuccessModal(true)
        track({
          event: 'Request Nonprofit Validation Submitted',
          properties: {
            userId: account?.id
          }
        })
      }
    },
    onError: () => {
      setOpenErrorModal(true)
    }
  })
  const { logError } = useLogger()

  const {
    errors,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    setFieldValue,
    setFieldTouched,
    dirty,
    isValid,
    isSubmitting
  } = useFormik({
    initialValues: {
      organizationName: prefill.name,
      email: '',
      organizationCountry: initialCountry,
      organizationWebsite: '',
      registryId: '',
      registryName: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        organizationName: validateString(),
        email: validateEmail(true),
        organizationCountry: validateString(),
        registryName: validateString({ optional: true }),
        registryId: validateString(),
        organizationWebsite: validateUrl(false, ORGANISATION_WEBSITE_MAX_LENGTH)
      }),
    onSubmit: async data => {
      try {
        await mutateAsync({
          organisationDetails: {
            name: data.organizationName,
            registryId: data.registryId,
            registryName: data.registryName || undefined,
            websiteUrl: data.organizationWebsite,
            email: data.email || undefined,
            countryCode: data.organizationCountry
          }
        })
      } catch (e) {
        logError(e)
      }
    },
    validateOnBlur: true,
    validateOnChange: true
  })

  const buttonDisabled = !dirty || !isValid || isSubmitting

  return (
    <FullPageFormLayout
      actionsHeader={
        <WizardHeader
          title={t('workplace_giving.nonprofit.request.title')}
          onCancel={() => {
            if (dirty) {
              setOpenCancellationModal(true)
            } else {
              navigate(-1)
            }
          }}
          onPrimaryBtn={handleSubmit}
          isPrimaryBtnDisabled={buttonDisabled}
          isPrimaryBtnLoading={isSubmitting}
          primaryBtnLabel={t('workplace_giving.common.submit')}
        />
      }
    >
      <Heading level="h3" className={classes.SubTitle}>
        {t('workplace_giving.nonprofit.request.subtitle')}
      </Heading>
      <RequestNonprofitWizardForm
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
      />
      <RequestNonprofitModal
        type="success"
        handleClose={() => {
          setOpenSuccessModal(false)
          navigate(`/`)
        }}
        handleSubmit={handleSubmit}
        openModal={openSuccessModal}
      />
      <RequestNonprofitModal
        type="error"
        handleClose={() => {
          setOpenErrorModal(false)
        }}
        openModal={openErrorModal}
      />
      <RequestNonprofitModal
        type="cancel"
        handleClose={() => setOpenCancellationModal(false)}
        handleSubmit={() => {
          setOpenCancellationModal(false)
          navigate(-1)
        }}
        openModal={openCancellationModal}
      />
    </FullPageFormLayout>
  )
}
